// The lawyer's copy certification with the Ordem, run by the pt-certificate
// service through the lawyer backend. A preview fills the act in and stops on
// the register's confirmation page; a registration registers exactly what the
// lawyer approved from that preview. Each call takes about a minute, and the
// service writes its steps to the case meanwhile (passport_certification_run).

import { errorMessageFrom, requestBlob } from './client';

/** One step the service has reached, as written to the case. */
export interface CertificationStep {
  key: string;
  text: string;
  detail?: string | null;
  at?: string;
}

/** The run in progress or last finished, overwritten by each new run. */
export interface CertificationRun {
  mode: 'preview' | 'register';
  state: 'running' | 'done';
  started_at?: string;
  finished_at?: string;
  status?: string;
  reason?: string | null;
  steps: CertificationStep[];
}

/** The outcome on the case: a registered act, or why the last run stopped. */
export interface PassportCertificationRecord {
  status: 'registered' | 'registering' | 'failed' | 'needs_review' | 'refused' | 'approval_outdated' | string;
  registration_number?: string;
  consultation_code?: string;
  registered_at?: string;
  adopted?: boolean;
  resumo?: string;
  gcs_path?: string;
  error?: string;
  reason?: string;
  document?: { number?: string; holder?: string };
}

/** What the lawyer approves, exactly as the preview returned it. */
export interface CertificationApproval {
  number: string;
  description: string;
  sheets: number;
}

/** The service's answer. `status` is always there; `reason` when it stopped. */
export interface CertificationOutcome extends Partial<CertificationApproval> {
  status: string;
  reason?: string | null;
  resumo?: string;
  /** The register already holds an act for this document. */
  adopted?: boolean;
  registration_number?: string;
}

const apiUrl = (path: string) => `${process.env.REACT_APP_API_URL}${path}`;

const post = async (token: string, path: string, body?: CertificationApproval): Promise<CertificationOutcome> => {
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    return { status: 'failed', reason: 'The portal could not reach the backend. Check the connection and try again.' };
  }
  const data = await response.json().catch(() => null);
  if (data && typeof data.status === 'string') return data as CertificationOutcome;
  // A refusal from the backend itself (403, 404, 422) carries `detail`.
  return {
    status: response.status === 403 ? 'forbidden' : 'failed',
    reason: errorMessageFrom(data, `The certification answered ${response.status}.`),
  };
};

export const previewPassportCertification = (token: string, caseId: string) =>
  post(token, `/cases/${encodeURIComponent(caseId)}/passport-certification/preview`);

export const registerPassportCertification = (token: string, caseId: string, approval: CertificationApproval) =>
  post(token, `/cases/${encodeURIComponent(caseId)}/passport-certification/register`, approval);

export const fetchPassportCertificationPdf = (token: string, caseId: string): Promise<Blob> =>
  requestBlob(token, `/cases/${encodeURIComponent(caseId)}/passport-certification/pdf`, 'Could not load the act');

export const fetchPassportCertificationThumbnail = (token: string, caseId: string): Promise<Blob> =>
  requestBlob(token, `/cases/${encodeURIComponent(caseId)}/passport-certification/thumbnail`, 'No preview');
